import toastTrigger from '@ui/toast/toast-trigger';
import { setStorageItem, setStorageObject } from '../storage/local-storage';
import { HvHostMap, parseHvPath } from '@/connection/helpers';
import { BrandliveEvent } from '@/types/projects.types';
import {
  SingleSignOnTypes,
  SSOAttributeRule,
  SSOAttributeRuleCondition,
  SSOAttributeRuleConditionOperator,
  SSOAttributeRuleType,
  SSOIntegrationProviderType,
  SSOOptionsType,
} from '@/types/registration.types';
import { getRandomValue } from '../random-value';
import { v4 as uuidv4 } from 'uuid';

//export const customSSOTypes = [SingleSignOnTypes.SAML, SingleSignOnTypes.OIDC, SingleSignOnTypes.oAuth];
export const socialSSOTypes: Array<string> = [
  SingleSignOnTypes.Google,
  SingleSignOnTypes.Facebook,
  SingleSignOnTypes.LinkedIn,
];

const includesSocial = (type: string): boolean => socialSSOTypes.includes(type);

export const hasAnyConfiguredCustomSSO = (event: BrandliveEvent | null): boolean => {
  const singleSignOn = event?.registration_settings?.singleSignOn;
  if (!singleSignOn) return false;
  return singleSignOn.isOn && !!singleSignOn.singleSignOns?.length && !singleSignOn.singleSignOns?.some(includesSocial);
};

export const isUsingCustomSSO = (event: BrandliveEvent | null): boolean => {
  const singleSignOn = event?.registration_settings?.singleSignOn;
  if (!singleSignOn) return false;
  return singleSignOn.isOn && !singleSignOn.singleSignOns?.some(includesSocial);
};

export const isCustomSignOnType = (type: string): boolean => {
  return !socialSSOTypes.includes(type);
};

export const getSSOLogoutUrl = (event: BrandliveEvent | null): string | null => {
  const publicOauthIntegration = event?.channel_settings?.oauth2Integration;

  if (!event || !isUsingCustomSSO(event) || !publicOauthIntegration?.logout_url) {
    return null;
  }

  return publicOauthIntegration.logout_url.replace('${EVENT_URL}', window.location.href);
};

export interface SsoConfig {
  user_pool_client_id: string;
  idp_name: string;
  integration_provider?: string;
}
export const handleSSOSignIn = (ssoConfig: SsoConfig, channel?: number, domain?: string) => () => {
  const client_id = ssoConfig.user_pool_client_id;

  // either domain or channel is required
  if (!channel && !domain) {
    toastTrigger(
      <div>
        <h6>SSO failed</h6>
        <p>SSO is not enabled for this channel.</p>
      </div>,
      { type: 'error' }
    );
    return;
  }

  setStorageObject('sso_admin_auth', { client_id, channel, domain });

  const params = new URLSearchParams({
    redirect_uri: `${location.origin}/sso-redirect`,
    client_id: client_id,
    identity_provider: ssoConfig.idp_name,
    response_type: 'code',
  });

  let baseUrl = import.meta.env.VITE_COGNITO_URL;

  if (ssoConfig.integration_provider === SSOIntegrationProviderType.JACKSON) {
    const state = getRandomValue();
    setStorageItem(`sso_state`, state, 1);

    params.set('state', state);

    baseUrl = `${import.meta.env.VITE_SSO_HOST}/sso-redirect`;
  }

  window.location.href = `${baseUrl}?${params}`;
};

export const handleOauth2Siginin = (channel: number) => async () => {
  const state = getRandomValue();
  setStorageItem(`sso_state`, state, 1);
  setStorageObject('sso_admin_auth', { channel, oauth2: true });

  const params = new URLSearchParams({ redirect_uri: `${location.origin}/sso-redirect`, state });
  window.location.href = await parseHvPath(HvHostMap.authorization, `/e3-oauth-sign-in/${channel}?${params}`);
};

export const handleOauth2SigininByDomain = (uuid: string, domain: string) => async () => {
  const state = getRandomValue();
  setStorageItem(`sso_state`, state, 1);
  setStorageObject('sso_admin_auth', { domain, oauth2: true });

  const params = new URLSearchParams({ redirect_uri: `${location.origin}/sso-redirect`, state });
  window.location.href = await parseHvPath(HvHostMap.authorization, `/e3-oauth-sign-in-by-domain/${uuid}?${params}`);
};

export const getDefaultSSOAttributeRule = (userId: number | undefined): SSOAttributeRule => {
  return {
    uuid: uuidv4(),
    name: '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: userId,
    updatedBy: userId,
    enabled: true,
    type: SSOAttributeRuleType.ALLOW,
    mustMeetAllConditions: true,
    conditions: [getDefaultSSOAttributeRuleCondition()],
  };
};

export const getDefaultSSOAttributeRuleCondition = (): SSOAttributeRuleCondition => {
  return {
    attribute: '',
    operator: SSOAttributeRuleConditionOperator.EQUALS,
    value: '',
  };
};

export const getCustomAttributesFromSSO = (sso: SSOOptionsType): string[] => {
  const attributes: string[] = [];

  Object.entries(sso.attribute_mapping).forEach(([key, value]) => {
    if (key.startsWith('reg_question.')) {
      attributes.push(value);
    }
  });

  return attributes;
};

export const isValidAttributeRule = (rule: SSOAttributeRule, sso: SSOOptionsType): boolean => {
  return (
    rule.name.length > 0 &&
    rule.conditions.length > 0 &&
    rule.conditions.every((condition) => isValidAttributeRuleCondition(condition, sso))
  );
};

export const isValidAttributeRuleCondition = (condition: SSOAttributeRuleCondition, sso: SSOOptionsType): boolean => {
  return (
    condition.operator.length > 0 &&
    (condition.value.length > 0 ||
      [SSOAttributeRuleConditionOperator.BOOLEAN_FALSE, SSOAttributeRuleConditionOperator.BOOLEAN_TRUE].includes(
        condition.operator
      )) &&
    Object.values(sso.attribute_mapping).includes(condition.attribute)
  );
};
